import { AbsgArrayHelper, CamelMapHelper, LogService } from '@absg/angular-common';
import {
  AssignedProductDetail,
  MedicalEquivalentApi,
  ProductDetail,
} from '@absg/api-formulary';
import { InventoryItemSummary, PendingInvItem, PendingTransferResponse, TransferApi } from '@absg/api-inventory';
import { LookupDetail } from '@absg/api-lookup';
import { PatientLite } from '@absg/api-party';
import { StationLite } from '@absg/api-station-management';
import {
  BaseService, CalculationHelper, ContainerTypes, InventoryTransactionTypes, ManagePosition, PatientService as PartyPatientService,
  PickListItem, PositionAssignment, RuleContainerSetting, StorageAccessPoint, StorageCabinet, StoragePosition,
  StorageStation, StringConstants,
} from '@absg/nim-client-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ManagePositionsCoreService } from 'core.services/manage-positions-core.service';
import { StationInfoService } from 'core.services/station-info.service';
import { ManagePositionHelper } from 'inventory.root/shared/helpers/manage-position-helper';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, concatMap, map, take, tap } from 'rxjs/operators';
import { InventoryItemProductLot } from 'shared.models/inventory-item-product-lot.model';
import { CabinetTypes } from 'shared.root/enums/cabinet-types.enum';
import { LookupService } from 'shared.root/services/lookup.service';
import { PositionService } from 'shared.root/services/position.service';
import { SettingsService } from 'shared.root/services/settings.service';
import { FormularyManagementService } from 'shared.root/services/state-management/formulary-management.service';

import { InventoryEventBuilderService } from './inventory-event-builder.service';

@Injectable({ providedIn: 'root' })
export class ManagePositionsService extends BaseService {
  selectedManagePosition: BehaviorSubject<ManagePosition> = new BehaviorSubject<ManagePosition>(null);
  private ruleCategories = ['item settings', 'properties'];
  managePositionSubscription: Subscription;
  loadingPositions = new BehaviorSubject<boolean>(false); // allows components to show spinner faster
  cachedPositionSettings: RuleContainerSetting[] = [];
  private hasAnnexStations = false;
  patientOwnedLookupTypes: PickListItem[];
  annexStations: StationLite[] = [];

  stationPosition: StoragePosition[];
  stationAccessPoint: StorageAccessPoint[];
  stationCabinet: StorageCabinet[];
  constructor(
    private lookupService: LookupService,
    loggingService: LogService,
    private stationInfoService: StationInfoService,
    private settingsService: SettingsService,
    private inventoryEventBuilderService: InventoryEventBuilderService,
    private medicalEquivalentApi: MedicalEquivalentApi,
    private formularyManagementService: FormularyManagementService,
    private partyPatientService: PartyPatientService,
    private transferApi: TransferApi,
    private managePositionsCoreService: ManagePositionsCoreService,
    private positionService: PositionService,
  ) {
    super(loggingService);
  }

  setSelectedManagePosition(managePosition: ManagePosition): void {
    this.selectedManagePosition.next(managePosition);
  }

  getSelectedManagePosition(): Observable<ManagePosition> {
    return this.selectedManagePosition.asObservable();
  }

  getPracticeExpirationDateWarningDay(): number {
    return this.stationInfoService.getPracticeExpirationDateWarningDay();
  }

  getPendingTransfers(fromStationContainerId: number, spIds: number[], patientId: number, patientOwnedTypeId: number): Observable<PendingTransferResponse[]> {
    return this.transferApi.getPendingTransfers({ fromStationContainerId, supplierProductInclusionIds: spIds, patientId, patientOwnedTypeId })
      .pipe(
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      );
  }

  getManagePositionsForCurrentStation(): Observable<ManagePosition[]> {
    this.stationInfoService.clearInventoryCache();
    const stationInfo = this.stationInfoService.station.getValue();
    return this.stationInfoService.getAssignedInventoryItems()
      .pipe(
        tap(() => this.loadingPositions.next(true)),
        concatMap((items: InventoryItemSummary[]) => {
          if (!AbsgArrayHelper.isEmpty(items)) {
            const positionIds: number[] = AbsgArrayHelper.map(items, i => i.positionContainerId);
            const supplierProductInclusionIds: number[] = this.getSupplierProductInclusionIds(items);
            const patientIds = this.getPatientIds(items);
            const patientApiCall = this.partyPatientService.getPatientListByIds(patientIds);
            return forkJoin([
              this.formularyManagementService.getPracticeAssignedProductDetails(supplierProductInclusionIds),
              this.lookupService.getPatientOwnedItemTypeDetailList(),
              patientApiCall,
              this.getAnnexStationsByPractice(),
              this.settingsService.populateXrefs(positionIds, supplierProductInclusionIds),
            ]).pipe(
              catchError((error: HttpErrorResponse) => this.handleErrorResponse(error)),
              concatMap(([products, lookupTypes, patientInfo]) => {
                this.cachedPositionSettings = this.settingsService.convertToInternalModals(this.settingsService.stationDefaults.getValue());
                let mappedPatientList: PatientLite[] = [];
                mappedPatientList = !AbsgArrayHelper.isEmpty(patientInfo) ? CamelMapHelper.camelizeKeys(patientInfo) : [];
                const managePositions = this.assembleManagePositions(products, items, stationInfo, mappedPatientList, lookupTypes);
                return of(managePositions);
              }),
            );
          } else {
            return this.getAnnexStationsByPractice().pipe(map(() => null));
          }
        }),
        tap(() => this.loadingPositions.next(false)),
      );
  }

  getManagePositionsForCurrentStationAndPositions(): Observable<(ManagePosition[] | PositionAssignment[])[]> {
    this.stationInfoService.clearInventoryCache();
    const stationInfo = this.stationInfoService.station.getValue();
    return this.stationInfoService.getAssignedInventoryItems()
      .pipe(
        tap(() => this.loadingPositions.next(true)),
        concatMap((items: InventoryItemSummary[]) => {
          if (!AbsgArrayHelper.isEmpty(items)) {
            const positionIds: number[] = AbsgArrayHelper.map(items, i => i.positionContainerId);
            const supplierProductInclusionIds: number[] = this.getSupplierProductInclusionIds(items);
            const patientIds = this.getPatientIds(items);
            const patientApiCall = this.partyPatientService.getPatientListByIds(patientIds);
            return forkJoin([
              this.formularyManagementService.getPracticeAssignedProductDetails(supplierProductInclusionIds),
              this.lookupService.getPatientOwnedItemTypeDetailList(),
              patientApiCall,
              this.getAnnexStationsByPractice(),
              this.settingsService.populateXrefs(positionIds, supplierProductInclusionIds),
            ]).pipe(
              catchError((error: HttpErrorResponse) => this.handleErrorResponse(error)),
              concatMap(([products, lookupTypes, patientInfo]) => {
                let mappedPatientList: PatientLite[] = [];
                mappedPatientList = !AbsgArrayHelper.isEmpty(patientInfo) ? CamelMapHelper.camelizeKeys(patientInfo) : [];
                this.cachedPositionSettings = this.settingsService.convertToInternalModals(this.settingsService.stationDefaults.getValue());
                const managePositions = this.assembleManagePositions(products, items, stationInfo, mappedPatientList, lookupTypes);
                const positions = this.positionService.convertToViewModel(this.stationInfoService.station.getValue(), items, products, mappedPatientList, lookupTypes);
                return of([managePositions, positions]);
              }),
            );
          } else {
            return this.getAnnexStationsByPractice().pipe(map(() => [null as ManagePosition[], [] as PositionAssignment[]]));
          }
        }),
        tap(() => this.loadingPositions.next(false)),
      );
  }

  saveInventoryEventAsync(autoReorderSettingChange: boolean, isBinItemChange: boolean, isBinItem: boolean,
    autoReorderFlag?: boolean, itemMax?: number, itemMin?: number,
    supplierInclusion?: AssignedProductDetail, isPreferredNdc?: boolean, updatedManagePositionItem: ManagePosition = null): Observable<void> {
    return this.getSelectedManagePosition()
      .pipe(take(1),
        concatMap(managePosition => {
          if (updatedManagePositionItem !== null) {
            managePosition = updatedManagePositionItem;
          }
          if ((managePosition === null) || (managePosition.inventoryItem === null)) {
            throw new Error('createInventoryEvent will Not accept a Null Manage position or InventoryItem');
          }
          if (managePosition.isPatientOwnedStatus()) {
            return of(null);
          }
          if (itemMin === null) {
            itemMin = Number(this.getCurrentPositionMin(managePosition));
          }
          if (itemMax === null) {
            itemMax = Number(this.getCurrentPositionMax(managePosition));
          }
          if (autoReorderFlag === null) {
            autoReorderFlag = this.getCurrentPositionAutoReorder(managePosition);
          }
          const quantityOnHand = managePosition.inventoryItem.quantityOnHand;

          const positionContainerId = managePosition.inventoryItem.positionContainerId;
          let transactionTypeId = autoReorderSettingChange ?
            InventoryTransactionTypes.ManagePositionAutoReorder : InventoryTransactionTypes.ManagePositionMinMax;
          let transactionTypeName = autoReorderSettingChange ? 'Manage Position AutoReorder Event' : 'Manage Position MinMax Event';
          if (isBinItemChange) {
            transactionTypeId = InventoryTransactionTypes.BinPrimaryItemChange;
            transactionTypeName = 'Manage Position Bin Primary Item Change';
          }
          const product = isBinItem ? supplierInclusion : managePosition.productDetail;

          const inventoryItemId = managePosition.inventoryItem.inventoryItemId;
          return this.inventoryEventBuilderService.createInventoryEventAsync(autoReorderFlag, product, itemMax,
            itemMin, quantityOnHand, positionContainerId, transactionTypeId, transactionTypeName, inventoryItemId,
            isBinItem, isPreferredNdc, supplierInclusion);
        }));
  }

  getAssignedItemManagePositions(itemsWithPendingQty: PendingInvItem[], positions: ManagePosition[]): ManagePosition[] {
    const byProductInclusionId = new Map<number, PendingInvItem>();
    for (const item of itemsWithPendingQty) {
      byProductInclusionId.set(item.supplierProductInclusionId, item);
    }
    positions.forEach(position => {
      if (position && position.isBin) {
        position.inventoryItems.forEach(invItem => { this.populatePendingQuantity(invItem, byProductInclusionId); });
      } else if (position) {
        this.populatePendingQuantity(position.inventoryItem, byProductInclusionId);
      }
    });
    return positions;
  }

  getCurrentQuantityOnHandString(
    managePosition: ManagePosition, defaultValue: string = StringConstants.outOfStock, overrideBinLogic = false,
  ): string {
    let currentQuantityString = '';
    let quantityOnHand = 0;
    if (managePosition) {
      quantityOnHand = (managePosition.isBin && !overrideBinLogic) ? this.getCurrentQuantityOnHandForBinItem(managePosition)
        : this.getCurrentQuantityOnHand(managePosition, overrideBinLogic);

      if (quantityOnHand !== 0) {
        currentQuantityString = CalculationHelper.convertToQuantityString(quantityOnHand,
          managePosition.productDetail.dFactor,
          managePosition.productDetail.iFactor);
      } else {
        currentQuantityString = defaultValue;
      }
    }

    return currentQuantityString;
  }

  getCurrentQuantityOnHandStringInDispense(managePosition: ManagePosition, defaultValue: string = StringConstants.outOfStock): string {
    let currentQuantityString = '';
    let quantityOnHand = 0;
    if (managePosition) {
      quantityOnHand = managePosition.isBin ? this.getCurrentQuantityOnHandForBinItem(managePosition)
        : this.getCurrentQuantityOnHand(managePosition);

      if (quantityOnHand !== 0) {
        currentQuantityString = quantityOnHand.toString();
      } else {
        currentQuantityString = defaultValue;
      }
    }

    return currentQuantityString;
  }

  getCurrentQuantityOnHandForBinItem(managePosition: ManagePosition): number {
    return ManagePositionHelper.getCurrentQuantityForBinItemInStation(managePosition)
      + this.getCurrentQuantityUnderHoodForBinItem(managePosition);
  }

  getCurrentQuantityOnHand(managePosition: ManagePosition, overrideBinLogic = false): number {
    if (AbsgArrayHelper.isNil(managePosition)) {
      return 0;
    }

    return managePosition.witnessRequired ? ManagePositionHelper.getCurrentQuantityInStation(managePosition, overrideBinLogic) :
      CalculationHelper.add(ManagePositionHelper.getCurrentQuantityInStation(managePosition, overrideBinLogic),
        ManagePositionHelper.getCurrentQuantityInUnderhood(managePosition, overrideBinLogic));
  }

  getCurrentQuantityUnderHood(managePosition: ManagePosition): number {
    return ManagePositionHelper.getCurrentQuantityInUnderhood(managePosition);
  }

  getCurrentQuantityUnderHoodForBinItem(managePosition: ManagePosition): number {
    let quantity = 0;
    if (managePosition && managePosition.productDetails) {
      for (const productDetail of managePosition.productDetails) {
        const matchingCachedItem: InventoryItemSummary =
          this.getMatchingUnderhoodInventoryItemForBinItem(productDetail.supplierProductInclusionId);
        if (matchingCachedItem) {
          quantity += AbsgArrayHelper.sumBy(matchingCachedItem.inventoryItemProducts, (iip) => iip.lotQuantityUnderhood);
        }
      }
    }
    return quantity;
  }
  getCurrentQuantityUnderHoodString(managePosition: ManagePosition, defaultValue = '0'): string {
    let currentQuantityString = '';

    if (managePosition) {
      const quantityUnderHood = this.getCurrentQuantityUnderHood(managePosition);

      if (quantityUnderHood !== 0) {
        currentQuantityString = CalculationHelper.convertToQuantityString(quantityUnderHood,
          managePosition.productDetail.dFactor,
          managePosition.productDetail.iFactor);
      } else {
        currentQuantityString = defaultValue;
      }
    }

    return currentQuantityString;
  }

  getCurrentQuantityUnitsString(managePosition: ManagePosition): string {
    if (managePosition) {
      if (this.getCurrentQuantityOnHandString(managePosition).toLowerCase() === StringConstants.outOfStock.toLowerCase()) {
        return '';
      } else {
        return managePosition.productDetail.iUomDisplay;
      }
    }
  }

  getCurrentQuantityOnHandWithInvUOMString(managePosition: ManagePosition): string {
    let qoh = this.getCurrentQuantityOnHandString(managePosition);
    if (qoh !== '') {
      qoh = `${qoh} ${this.getCurrentQuantityUnitsString(managePosition)}`;
    }

    return qoh;
  }

  setPositionRules(positions: ManagePosition[]): Observable<ManagePosition[]> {
    const positionIds = [];
    const itemIds = [];
    AbsgArrayHelper.each(positions, position => {
      if (position.storagePosition) {
        positionIds.push(position.storagePosition.id);
      }
      itemIds.push(position.productDetail.supplierProductInclusionId);
    });
    return this.settingsService.populateXrefs(positionIds, itemIds).pipe(map(() => this.setSettingsToManagePosition(positions)));
  }

  applySettingRuleUpdates(managePosition: ManagePosition): void {
    AbsgArrayHelper.each(managePosition.positionRules, (rule) => {
      rule.containerId = managePosition.storagePosition.id;
      const name = rule.rule.ruleCategory.name.toLocaleLowerCase();
      const ruleType = rule.rule.dataType ? rule.rule.dataType.toLocaleLowerCase() : null;
      if (this.ruleCategories.includes(name) && ruleType === 'bool') {
        const supplierProductInclusionId = this.getSupplierProductInclusionId(managePosition);
        this.settingsService.ruleContainerAdjustment(rule, supplierProductInclusionId);
      }
    });
  }

  setSettingsToManagePosition(positions: ManagePosition[]): ManagePosition[] {
    AbsgArrayHelper.each(positions, (position) => {
      const containerId = position.inventoryItem.positionContainerId;
      position.isBin = this.settingsService.getBinSetting(containerId);
      const supplierProductInclusionId = this.getSupplierProductInclusionId(position);
      position.witnessRequired = this.settingsService.getWitnessRequiredSetting(containerId, supplierProductInclusionId);
      position.verifyQOH = this.settingsService.getVerifyQOHSetting(containerId, supplierProductInclusionId);
      position.isLotTrackingOn = this.settingsService.getLotTrackingSetting(containerId);
      position.dispenseType = this.settingsService.getDispenseTypeIdSetting(containerId);
      // TODO: When we consolidate objects as part of simplification we should be able to
      // pull preferred here and not need to get it again when getting auto reorder
    });
    return positions;
  }

  getPreferredItemSetting(containerId: number, itemId: string): boolean {
    return this.managePositionsCoreService.getPreferredItemSetting(containerId, itemId);
  }

  getWitnessRequiredSetting(containerId: number, itemId: number): boolean {
    return this.settingsService.getWitnessRequiredSetting(containerId, itemId);
  }

  // Include when date is null, moving null dates further to the end
  // Ex: [40, 100] will return a negative value, sorting 40 as less than 100
  sortList(lotItems: InventoryItemProductLot[]): InventoryItemProductLot[] {
    return ManagePositionHelper.sortList(lotItems);
  }

  sortDates(a: InventoryItemProductLot, b: InventoryItemProductLot): number {
    return ManagePositionHelper.sortDates(a, b);
  }

  getSortedLotAndExpirationList(managePositions: ManagePosition): InventoryItemProductLot[] {
    return ManagePositionHelper.getSortedLotAndExpirationList(managePositions);
  }

  combineMedicallyEquivalent(managePositions: ManagePosition[]): ManagePosition[] {
    const idArray = [];
    const listToReplace: ManagePosition[] = [];
    AbsgArrayHelper.each(managePositions, (managePosition, index) => {
      idArray.push(managePosition.storagePosition);
      const foundIndex = AbsgArrayHelper.findIndex(idArray, (position) => position.name === managePosition.storagePosition.name);
      if (foundIndex < index) {
        listToReplace.push(managePosition);
        managePosition.shouldDisplay = false;
        if (!AbsgArrayHelper.isNil(managePositions[foundIndex]) && managePositions[foundIndex].inventoryItems.length === 0) {
          managePositions[foundIndex].inventoryItems.push(managePositions[foundIndex].inventoryItem);
          managePositions[foundIndex].productDetails.push(managePositions[foundIndex].productDetail);
        }
        managePositions[foundIndex].inventoryItems.push(managePosition.inventoryItem);
        managePositions[foundIndex].productDetails.push(managePosition.productDetail);
      } else if (foundIndex === index) {
        if (managePositions[foundIndex].inventoryItems.length === 0) {
          managePositions[foundIndex].inventoryItems.push(managePositions[foundIndex].inventoryItem);
          managePositions[foundIndex].productDetails.push(managePositions[foundIndex].productDetail);
        }
      }
    });
    return AbsgArrayHelper.filter(managePositions, (position) => position.shouldDisplay);
  }

  getCurrentQuantityUnderHoodByLotNumber(managePosition: ManagePosition, lotNumber: string): number {
    const quantity = 0;
    if (managePosition) {
      const matchingCachedItem: InventoryItemSummary = this.getMatchingUnderhoodInventoryItem(managePosition);
      if (matchingCachedItem) {
        for (const iip of matchingCachedItem.inventoryItemProducts) {
          if (lotNumber === iip.lotNumber) {
            return iip.lotQuantityUnderhood;
          }
        }
      }
    }
    return quantity;
  }

  getUnderHoodQuantityExcludingLotNumber(managePosition: ManagePosition, arrInventoryItemProductLot: InventoryItemProductLot[],
    item: InventoryItemProductLot): number {
    let quantity = 0;
    for (const iip of arrInventoryItemProductLot) {
      if (item.lotNumber !== iip.lotNumber) {
        if (iip.isAudited) {
          quantity += iip.unsavedUnderHoodLotQuantity;
        } else { // TODO: Check for Refactor
          quantity += this.getCurrentQuantityUnderHoodByLotNumber(managePosition, iip.lotNumber);
        }
      }
    }
    return quantity;
  }

  isQOHPartial(managePosition: ManagePosition): boolean {
    let status = false;
    if (managePosition) {
      const qoh = +this.getCurrentQuantityOnHandString(managePosition);
      if (qoh > 0) {
        status = CalculationHelper.subtract(qoh, Math.floor(qoh)) !== 0;
      }
    }
    return status;
  }

  getPrintReceiptForRestockItem(containerId: number, supplierProductInclusionId: number): boolean {
    return this.settingsService.getReceiptForRestockSetting(containerId, supplierProductInclusionId);
  }

  getPreferredNDCItem(managePositions: ManagePosition[]): ManagePosition[] {
    if (managePositions && managePositions.length > 0) {
      const preferredItemId = this.settingsService.getPreferredItemIdSetting(managePositions[0].getPositionContainerId());

      AbsgArrayHelper.each(managePositions, managePosition => {
        if (managePosition.productDetail.itemId === preferredItemId) {
          managePosition.isPreferredNDC = true;
        }
      });
      return managePositions;
    }
  }
  /***
   * Returns the preferred Item productDetail if there is one
   * Or it will return the productDetail for the  cheapest item not found elsewhere on the station if no preferred
   */
  getAutoReorderItemIdForTransactionRequest(containerId: number,
    productId: number, productDetails: AssignedProductDetail[], toExclude?: number[], toInclude?: number[]): Observable<AssignedProductDetail> {
    // Has a preferred Item
    const preferredItemId = this.settingsService.getPreferredItemIdSetting(containerId);
    if (preferredItemId) {
      const productDetail: AssignedProductDetail = AbsgArrayHelper.find(productDetails, (product) => product.itemId === preferredItemId);
      if (!AbsgArrayHelper.isNil(productDetail)) {
        return of(productDetail);
      } else {
        return this.getAutoReorderItemDetail(containerId, productId, toExclude, toInclude);
      }
    } else {
      return this.getAutoReorderItemDetail(containerId, productId, toExclude, toInclude);
    }
  }

  getCurrentPositionMax(managePosition: ManagePosition): string {
    if (managePosition) {
      return this.settingsService.getMaxSetting(managePosition.getPositionContainerId()).toString();
    }
  }

  getCurrentPositionAutoReorder(managePosition: ManagePosition): boolean {
    if (managePosition) {
      const supplierProductInclusionId = managePosition.getSupplierProductInclusionId();
      return this.settingsService
        .getAutoReOrderSetting(managePosition.getPositionContainerId(),
          supplierProductInclusionId,
          managePosition.productDetail.formularyTypeId,
          managePosition.isPatientOwnedStatus());
    }
  }

  getCurrentPositionMin(managePosition: ManagePosition): string {
    if (managePosition) {
      return this.settingsService.getMinSetting(managePosition.getPositionContainerId()).toString();
    }
  }
  getCurrentQuantityOnHandForAllItems(managePosition: ManagePosition): number {
    return CalculationHelper.add(ManagePositionHelper.getCurrentQuantityInStationAllItems(managePosition),
      ManagePositionHelper.getCurrentQuantityInUnderhoodAllItems(managePosition));
  }

  private getAutoReorderItemDetail(containerId: number,
    productId: number, toExclude: number[], toInclude: number[]): Observable<AssignedProductDetail> {
    return this.stationInfoService.getCachedInventoryItems().pipe(take(1),
      concatMap((allInventoryItems) => {
        const nonPatientOwnedItems = AbsgArrayHelper.filter(allInventoryItems, i => AbsgArrayHelper.isNil(i.patientId));
        // Do not include any items assigned elsewhere in the station
        let excludeList = this.getSupplierProductInclusionIds(
          AbsgArrayHelper.filter(nonPatientOwnedItems, i => i.positionContainerId !== containerId));
        if (!AbsgArrayHelper.isNil(toExclude)) {
          excludeList = AbsgArrayHelper.union(excludeList, toExclude);
        }
        if (!AbsgArrayHelper.isNil(toInclude)) {
          excludeList = AbsgArrayHelper.difference(excludeList, toInclude);
        }
        const request = this.stationInfoService.generateGetCheapestEquivilantRequest(excludeList);
        return this.medicalEquivalentApi.getCheapestEquivilantForProduct(productId, request).pipe(take(1),
          map((product: ProductDetail) => ({
            supplierProductInclusionId: product.supplierProductInclusionId,
            itemId: product.itemId,
            tradeName: product.tradeName,
            dFactor: product.dispenseUnitOfMeasureConversionFactor,
            iFactor: product.inventoryUnitOfMeasureConversionFactor,
            pFactor: product.purchaseUnitOfMeasureConversionFactor,
            meiParentId: product.meiParentId,
            formularyTypeId: product.formularyTypeId,
          } as AssignedProductDetail)));
      }));
  }

  private getSupplierProductInclusionIds(inventoryItems: InventoryItemSummary[]): number[] {
    if (!inventoryItems) {
      return [];
    }
    const ids = inventoryItems
      .filter(inventoryItem => inventoryItem?.supplierProductInclusionId)
      .map(inventoryItem => inventoryItem.supplierProductInclusionId);

    return ids;
  }

  private assembleManagePositions(
    formularyItems: AssignedProductDetail[], inventoryItems: InventoryItemSummary[], stationInfo: StorageStation, patientList: PatientLite[], lookupTypes: LookupDetail[],
  ): ManagePosition[] {
    this.stationPosition = AbsgArrayHelper.flatMap(stationInfo.cabinets, s =>
      !AbsgArrayHelper.isNil(s.children) ? (AbsgArrayHelper.flatMap(s.children, c =>
        !AbsgArrayHelper.isNil(c.children) ? AbsgArrayHelper.flatMap(c.children, a => a) : [])) : []);
    this.stationAccessPoint = AbsgArrayHelper.flatMap(stationInfo.cabinets, s => !AbsgArrayHelper.isNil(s.children) ? s.children : []);
    this.stationCabinet = AbsgArrayHelper.flatMap(stationInfo.cabinets, s => s);

    const managePositions: ManagePosition[] = inventoryItems
      .map((inventoryItem: InventoryItemSummary) => this.getStationInfoForItem(formularyItems, inventoryItem, stationInfo, patientList, lookupTypes))
      .filter((populatedPosition: ManagePosition) =>
        // Catch for items belonging to deleted cabinets - do not display
        populatedPosition?.positionNumber > 0,
      );
    return managePositions;
  }

  private getStationInfoForItem(items: AssignedProductDetail[], inventoryItem: InventoryItemSummary, stationInfo: StorageStation, patientList: PatientLite[],
    lookupTypes: LookupDetail[]): ManagePosition {
    let cachedProductItems: AssignedProductDetail[] = items;
    if (!cachedProductItems) {
      cachedProductItems = [];
    }

    const matchingCachedItem: AssignedProductDetail = cachedProductItems.find((candidateItem: AssignedProductDetail) =>
      candidateItem.supplierProductInclusionId === inventoryItem.supplierProductInclusionId);

    let managePosition: ManagePosition = null;

    if (matchingCachedItem) {
      const matchingPosition: StoragePosition = this.stationPosition.find((candidatePosition: StoragePosition) => candidatePosition.id === inventoryItem.positionContainerId);

      managePosition = new ManagePosition();
      managePosition.inventoryItem = inventoryItem;
      managePosition.productDetail = matchingCachedItem;
      managePosition.stationId = stationInfo.id;
      managePosition.stationName = stationInfo.name;
      if (matchingPosition) {
        const matchingAccessPoint: StorageAccessPoint = this.stationAccessPoint.find(a => a.id === matchingPosition.parentContainerId);
        const matchingCabinet: StorageCabinet = this.stationCabinet.find(a => a.id === matchingAccessPoint?.parentContainerId);
        managePosition.storagePosition = matchingPosition;
        if (matchingPosition.containerType && matchingPosition.containerType.id === ContainerTypes.LimitedAccessDrawer) {
          const ladAccessPoint = this.stationInfoService.getLadById(matchingPosition.id);
          const sequence: number = ladAccessPoint.sequence;
          managePosition.positionNumber = sequence;
          managePosition.storagePosition.isPhysicalPosition = true;
          managePosition.isLimitedDrawer = true;
          managePosition.storagePosition.isSimulateMode = !!ladAccessPoint.isSimulateMode;
        } else {
          const sequence: number = matchingPosition.sequence;
          managePosition.positionNumber = sequence;
          const doorName = matchingAccessPoint?.name.toLowerCase();
          managePosition.storagePosition.isPhysicalPosition = !doorName.includes('virtual') && !doorName.includes('vpm');
          managePosition.storagePosition.isSimulateMode = !!matchingAccessPoint?.isSimulateMode;
        }
        switch (matchingCabinet?.containerType.id) {
          case CabinetTypes.Virtual:
            managePosition.cabinetSort = 3;
            break;
          case CabinetTypes.Refrigerator:
            managePosition.cabinetSort = 2;
            break;
          default:
            managePosition.cabinetSort = 1;
            break;
        }
      }

      managePosition.doorName = managePosition.getDoorName();
      managePosition.positionName = managePosition.getPositionName();
      // Populate Patient Owned item Information
      if (!AbsgArrayHelper.isNil(patientList) && patientList.length > 0 && inventoryItem.patientId > 0) {
        const patientData: PatientLite = patientList.find((x: { patientId: number }) => x.patientId === inventoryItem.patientId);
        managePosition.patientInfo = patientData;
        if (inventoryItem.patientOwnedItemTypeId && !AbsgArrayHelper.isNil(lookupTypes) && lookupTypes.length > 0) {
          const lookupItem = lookupTypes.find(x => x.listInclusionId === inventoryItem.patientOwnedItemTypeId);
          managePosition.patientOwnedItemTypeShortName = lookupItem.shortName;
          managePosition.patientOwnedItemTypeFullName = lookupItem.name;
        }
      }
      const containerId = managePosition.inventoryItem.positionContainerId;
      managePosition.isBin = this.settingsService.getBinSetting(containerId);
      const supplierProductInclusionId = managePosition.getSupplierProductInclusionId();
      managePosition.witnessRequired = this.settingsService.getWitnessRequiredSetting(containerId, supplierProductInclusionId);
      managePosition.verifyQOH = this.settingsService.getVerifyQOHSetting(containerId, supplierProductInclusionId);
      managePosition.isLotTrackingOn = this.settingsService.getLotTrackingSetting(containerId);
      managePosition.dispenseType = this.settingsService.getDispenseTypeIdSetting(containerId);
    }

    return managePosition;
  }

  setStationCabinetInfo(managePosition: ManagePosition): void {
    managePosition.storagePosition = new StoragePosition();
    const stationInfo: StorageStation = this.stationInfoService.station.getValue();
    if (stationInfo) {
      const storageCabinets: StorageCabinet[] = stationInfo.cabinets;
      if (Array.isArray(storageCabinets)) {
        AbsgArrayHelper.forEach(storageCabinets, (cabinet: StorageCabinet) => {
          this.setAccessPointInfo(managePosition, cabinet);
        });
      }
    }
  }

  setAccessPointInfo(managePosition: ManagePosition, cabinet: StorageCabinet): void {
    const storageAccessPoints: StorageAccessPoint[] = cabinet.children;

    if (Array.isArray(storageAccessPoints)) {
      AbsgArrayHelper.forEach(storageAccessPoints, (accessPoint: StorageAccessPoint) => {
        this.setStoragePositionInfo(managePosition, cabinet, accessPoint);
      });
    }
  }

  setStoragePositionInfo(managePosition: ManagePosition, cabinet: StorageCabinet, accessPoint: StorageAccessPoint): void {
    const storagePositions: StoragePosition[] = accessPoint.children;

    if (Array.isArray(storagePositions)) {
      const matchingPosition: StoragePosition = storagePositions.find((candidatePosition: StoragePosition) => candidatePosition.id === managePosition.inventoryItem.positionContainerId);

      if (matchingPosition) {
        // Keeping Access Point name for LAD so UI will still show with Associated Door
        managePosition.storagePosition = matchingPosition;
        if (matchingPosition.containerType && matchingPosition.containerType.id === ContainerTypes.LimitedAccessDrawer) {
          const ladAccessPoint = this.stationInfoService.getLadById(matchingPosition.id);
          const sequence: number = ladAccessPoint.sequence;
          managePosition.positionNumber = sequence;
          managePosition.storagePosition.isPhysicalPosition = true;
          managePosition.isLimitedDrawer = true;
          managePosition.storagePosition.isSimulateMode = !!ladAccessPoint.isSimulateMode;
        } else {
          const sequence: number = matchingPosition.sequence;
          managePosition.positionNumber = sequence;
          const doorName = accessPoint.name.toLowerCase();
          managePosition.storagePosition.isPhysicalPosition = !doorName.includes('virtual') && !doorName.includes('vpm');
          managePosition.storagePosition.isSimulateMode = !!accessPoint.isSimulateMode;
        }

        // determine the type of cabinet
        switch (cabinet.containerType.id) {
          case CabinetTypes.Virtual:
            managePosition.cabinetSort = 3;
            break;
          case CabinetTypes.Refrigerator:
            managePosition.cabinetSort = 2;
            break;
          default:
            managePosition.cabinetSort = 1;
            break;
        }
      }
    }
  }

  private getMatchingUnderhoodInventoryItem(managePosition: ManagePosition): InventoryItemSummary {
    return AbsgArrayHelper.find(this.getCachedInventoryItems(),
      (candidateItem: InventoryItemSummary) => candidateItem.supplierProductInclusionId === managePosition.productDetail.supplierProductInclusionId);
  }

  private getMatchingUnderhoodInventoryItemForBinItem(supplierProductInclusionId: number): InventoryItemSummary {
    return AbsgArrayHelper.find(this.getCachedInventoryItems(), (candidateItem: InventoryItemSummary) => candidateItem.supplierProductInclusionId === supplierProductInclusionId);
  }

  private getCachedInventoryItems(): InventoryItemSummary[] {
    let cachedInventoryItems: InventoryItemSummary[] = this.stationInfoService.getAssignedInventoryItems().getValue();
    if (!cachedInventoryItems) {
      cachedInventoryItems = [];
    }

    return cachedInventoryItems;
  }

  private populatePendingQuantity(invItem: InventoryItemSummary, byProductInclusionId: Map<number, PendingInvItem>): void {
    if (invItem && invItem.supplierProductInclusionId > 0 && byProductInclusionId.has(invItem.supplierProductInclusionId)) {
      const itemWithPendingQty = byProductInclusionId.get(invItem.supplierProductInclusionId);
      invItem.pendingQuantity = itemWithPendingQty.pendingQuantity;
      invItem.pendingAnnexTransferQuantity = itemWithPendingQty.pendingAnnexTransferQuantity;
      invItem.pendingOverstockQuantity = itemWithPendingQty.pendingOverstockQuantity;
    }
  }

  private getPatientIds(inventoryItems: InventoryItemSummary[]): number[] {
    let patientIds = [];
    if (Array.isArray(inventoryItems)) {
      patientIds = AbsgArrayHelper.map(AbsgArrayHelper.filter(inventoryItems, item => !AbsgArrayHelper.isNil(item.patientId)), inventoryItem => inventoryItem.patientId);
    }
    return patientIds;
  }

  private getSupplierProductInclusionId(managePosition: ManagePosition): number {
    return managePosition.getSupplierProductInclusionId();
  }

  getAnnexStationsByPractice(): Observable<void> {
    return this.stationInfoService.getAnnexStationsByPractice().
      pipe(take(1),
        map(stations => this.setAnnexStations(stations)),
      );
  }

  setAnnexStations(stations: StationLite[]): void {
    this.annexStations = stations;
    this.setAnnexStationsStatus();
  }

  checkAnnexItemsModalMode(): boolean {
    return this.stationInfoService.checkAnnexItemsModalMode(this.annexStations, this.stationInfoService.station.value.id);
  }

  getAnnexStationStatus(): boolean {
    return this.hasAnnexStations;
  }

  setAnnexStationsStatus(): void {
    this.hasAnnexStations = this.checkAnnexItemsModalMode();
  }
}
